import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Brain, CloudRain, Zap, HeartCrack, Users, Briefcase, Moon, Flame, ShieldAlert, Pill, User, UserX, Home, MessageCircle } from 'lucide-react';

const iconMap = {
  'brain': Brain,
  'cloud-rain': CloudRain,
  'zap': Zap,
  'heart-crack': HeartCrack,
  'users': Users,
  'briefcase': Briefcase,
  'moon': Moon,
  'flame': Flame,
  'shield-alert': ShieldAlert,
  'pill': Pill,
  'user': User,
  'user-x': UserX,
  'home': Home,
  'message-circle': MessageCircle
};

export default function MoodTagSelector({ selectedTags, onTagsChange, maxTags = 3 }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/mood-tags`);
      setTags(response.data);
    } catch (error) {
      console.error('Failed to fetch mood tags:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTag = (tagId) => {
    const isSelected = selectedTags.includes(tagId);
    if (isSelected) {
      onTagsChange(selectedTags.filter(t => t !== tagId));
    } else if (selectedTags.length < maxTags) {
      onTagsChange([...selectedTags, tagId]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-[#2D3748]">What's on your mind?</h3>
        <span className="text-xs text-[#718096]">Select up to {maxTags}</span>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {tags.map((tag) => {
          const Icon = iconMap[tag.icon] || MessageCircle;
          const isSelected = selectedTags.includes(tag.id);
          
          return (
            <button
              key={tag.id}
              data-testid={`mood-tag-${tag.id}`}
              onClick={() => toggleTag(tag.id)}
              disabled={!isSelected && selectedTags.length >= maxTags}
              className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-gray-200 hover:border-primary/50 text-[#718096] disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              <Icon size={18} className={isSelected ? 'text-primary' : 'text-[#A0AEC0]'} />
              <span className="text-sm font-medium truncate">{tag.label}</span>
            </button>
          );
        })}
      </div>
      
      {selectedTags.length > 0 && (
        <p className="text-xs text-[#718096] text-center">
          {selectedTags.length} of {maxTags} selected
        </p>
      )}
    </div>
  );
}
